import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileSignature, CheckCircle, Clock, Send, ExternalLink } from 'lucide-react';
import { API, getHeaders } from '../portalShared';

const STATUS = {
  draft: { label: 'Draft', tint: 'text-[#9CA3AF] border-white/10 bg-white/5', icon: FileSignature },
  sent: { label: 'Awaiting your signature', tint: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10', icon: Send },
  signed: { label: 'Signed — payment pending', tint: 'text-[#FF4343] border-[#FF4343]/30 bg-[#FF4343]/10', icon: Clock },
  paid: { label: 'Paid — kicking off', tint: 'text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10', icon: CheckCircle },
  executing: { label: 'In flight', tint: 'text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10', icon: CheckCircle },
  completed: { label: 'Completed', tint: 'text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10', icon: CheckCircle },
  cancelled: { label: 'Cancelled', tint: 'text-[#6B7280] border-white/10 bg-white/5', icon: FileSignature },
};

export default function MyProposals() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/portal/proposals`, { headers: getHeaders() })
      .then(r => setProposals(r.data.proposals || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="my-proposals" className="max-w-4xl">
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-white">My Proposals</h2>
        <p className="text-xs text-[#9CA3AF] mt-1">Statements of Work your Campaign Manager has built for you. Sign the pending ones to kick off execution.</p>
      </div>

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">Loading…</p>
      ) : proposals.length === 0 ? (
        <div className="text-center py-10 bg-[#111827] border border-white/10">
          <FileSignature className="w-8 h-8 text-[#6B7280] mx-auto mb-2" />
          <p className="text-sm text-[#9CA3AF]">No proposals yet.</p>
          <p className="text-xs text-[#6B7280] mt-1">Ping your Campaign Manager in Messages to get one started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {proposals.map(p => {
            const meta = STATUS[p.status] || STATUS.draft;
            const Icon = meta.icon;
            const canSign = ['sent', 'draft'].includes(p.status);
            return (
              <div key={p.id} className="bg-[#111827] border border-white/10" data-testid={`myprop-${p.id}`}>
                <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold text-white">{p.title}</span>
                      <span className={`text-[10px] font-mono uppercase border px-1.5 py-0.5 flex items-center gap-1 ${meta.tint}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#9CA3AF]">{p.line_items?.length || 0} line item{p.line_items?.length === 1 ? '' : 's'} · Created {new Date(p.created_at).toLocaleDateString()}</p>
                    <p className="text-[10px] text-[#6B7280] font-mono mt-0.5">
                      ${p.one_time_total_usd?.toLocaleString() || 0} one-time
                      {p.monthly_total_usd > 0 && <> + ${p.monthly_total_usd.toLocaleString()}/mo recurring</>}
                    </p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {canSign && p.sign_token && (
                      <a href={`/proposal?token=${p.sign_token}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`myprop-sign-${p.id}`}
                        className="text-[11px] px-3 py-1.5 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] flex items-center gap-1">
                        Review &amp; Sign <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                      data-testid={`myprop-expand-${p.id}`}
                      className="text-[11px] px-3 py-1.5 border border-white/20 text-white hover:bg-white/5">
                      {expanded === p.id ? 'Hide' : 'Details'}
                    </button>
                  </div>
                </div>
                {expanded === p.id && (
                  <div className="border-t border-white/10 p-4 bg-[#090E33]/50 space-y-3" data-testid={`myprop-details-${p.id}`}>
                    {p.description && <p className="text-xs text-[#9CA3AF] whitespace-pre-wrap">{p.description}</p>}
                    <div className="space-y-1">
                      {(p.line_items || []).map(li => (
                        <div key={li.key} className="flex items-center justify-between text-xs bg-white/5 px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 ${li.department === 'Media' ? 'bg-[#FF4343]/10 text-[#FF4343]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>{li.department || 'Ops'}</span>
                            <span className="text-white truncate">{li.label}</span>
                          </div>
                          <span className="font-mono text-white flex-shrink-0">
                            ${li.line_total_usd?.toLocaleString() || (li.price_usd * li.quantity).toLocaleString()}
                            {li.billing === 'monthly' && '/mo'}
                          </span>
                        </div>
                      ))}
                    </div>
                    {['paid', 'executing', 'completed'].includes(p.status) && p.tasks?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[#6B7280] mb-1.5">Execution progress</p>
                        <div className="space-y-1">
                          {p.tasks.map(t => (
                            <div key={t.id} className="flex items-center gap-2 text-xs bg-white/5 px-3 py-1.5">
                              <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 ${t.department === 'Media' ? 'bg-[#FF4343]/10 text-[#FF4343]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>{t.department}</span>
                              <span className="flex-1 text-white truncate">{t.label}</span>
                              <span className={`text-[10px] font-mono uppercase ${t.status === 'done' ? 'text-[#10B981]' : t.status === 'in_progress' ? 'text-yellow-400' : 'text-[#9CA3AF]'}`}>
                                {t.status.replace('_', ' ')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
